import React, { useState, useRef, useEffect } from "react";
import { FaChevronDown, FaCheck } from "react-icons/fa";

/**
 * Responsive dropdown + sidebar menu component.
 *
 * @component
 * @param {Object} props
 * @param {{ key: string, label: string, icon: React.ReactNode }[]} props.options - Array of menu options
 * @param {string} props.activeOption - Currently selected option key
 * @param {(key: string) => void} props.setActiveOption - Setter to update active menu
 */

export const Menus = ({ options, setActiveOption, activeOption }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedItem = options.find((item) => item.key === activeOption);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOptionClick = (key) => {
    setActiveOption(key);
    setIsOpen(false);
  };

  return (
    <div className="w-full md:w-1/6">
      {/* --- Mobile / Tablet Dropdown Menu --- */}
      <div className="relative w-full md:hidden" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between rounded-lg border border-gray-300 bg-white px-4 py-3 text-left text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
          <div className="flex items-center space-x-3">
            <span className="text-gray-500 text-xl">{selectedItem?.icon}</span>
            <span className="text-gray-900 font-medium">
              {selectedItem?.label}
            </span>
          </div>
          <FaChevronDown
            className={`h-5 w-5 text-gray-400 transform transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {isOpen && (
          <div className="absolute z-10 mt-2 w-full rounded-md bg-white shadow-lg border border-gray-200 animate-fade-in-sm">
            <ul className="max-h-60 overflow-auto py-1">
              {options.map((item) => (
                <li
                  key={item.key}
                  onClick={() => handleOptionClick(item.key)}
                  className="cursor-pointer select-none py-3 px-4 text-gray-900 hover:bg-indigo-50 hover:text-indigo-900 transition flex items-center space-x-3">
                  <span className="text-indigo-600">{item.icon}</span>
                  <span
                    className={`block truncate ${
                      item.key === activeOption
                        ? "font-semibold"
                        : "font-normal"
                    }`}>
                    {item.label}
                  </span>
                  {item.key === activeOption && (
                    <span className="ml-auto text-indigo-600">
                      <FaCheck className="h-4 w-4" />
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* --- Desktop View: Vertical Menu --- */}
      <div className="hidden md:flex flex-col w-full gap-2 mt-2">
        {options.map(({ key, icon, label }) => (
          <div
            key={key}
            onClick={() => setActiveOption(key)}
            className={`flex items-center gap-3 px-4 py-2 rounded-lg border cursor-pointer transition-all duration-200 shadow-sm ${
              activeOption === key
                ? "bg-black text-white border-black"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
            }`}>
            <div className="text-xl">{icon}</div>
            <span className="font-medium">{label}</span>
            {activeOption === key && (
              <FaCheck className="ml-auto text-white text-sm" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
